import { db } from '../firebase';
import { collection, addDoc, getDocs, query, orderBy, updateDoc, doc, arrayUnion, serverTimestamp } from 'firebase/firestore';

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

export interface ChatSession {
  id: string;
  userId: string;
  question: string;
  hexagram?: any;
  hexagrams?: any;
  response: any; // Main AI response (JSON object or string)
  responseTime?: number;
  model?: string;
  followUpQuestions: {
    question: string;
    response: any;
    timestamp: any;
  }[];
  timestamp?: any;
  createdAt: any;
  updatedAt: any;
}

const COLLECTION_NAME = 'chatHistory';

// Helper to sanitize data for Firestore (remove undefined)
const sanitizeForFirestore = (obj: any): any => {
  if (obj === undefined) {
    return null; // Replace undefined with null for Firestore compatibility
  }
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }
  if (Array.isArray(obj)) {
    return obj.map(sanitizeForFirestore);
  }
  if (obj instanceof Date) {
    return obj;
  }
  
  const newObj: any = {};
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      newObj[key] = sanitizeForFirestore(obj[key]);
    }
  }
  return newObj;
};

export const saveChatSession = async (userId: string, question: string, hexagrams: any, response: any) => {
  try {
    const sanitizedHexagrams = sanitizeForFirestore(hexagrams);
    const sanitizedResponse = sanitizeForFirestore(response);
    
    const sessionData = {
      userId,
      question,
      hexagram: sanitizedHexagrams,
      response: sanitizedResponse,
      followUpQuestions: [],
      timestamp: serverTimestamp(), // Match iOS 'timestamp' field
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      responseTime: 0,
      model: 'web-fallback'
    };
    
    // Use nested collection path: users/{userId}/chatHistory
    const docRef = await addDoc(collection(db, 'users', userId, COLLECTION_NAME), sessionData);
    return docRef.id;
  } catch (error) {
    console.error("Error saving chat session: ", error);
    throw error;
  }
};

export const addFollowUpToChat = async (userId: string, chatId: string, question: string, response: any) => {
  try {
    const sanitizedResponse = sanitizeForFirestore(response);
    const followUpData = {
      question,
      response: sanitizedResponse,
      timestamp: new Date() // Store as Date, Firestore will convert to Timestamp
    };

    // Use nested collection path: users/{userId}/chatHistory/{chatId}
    const chatRef = doc(db, 'users', userId, COLLECTION_NAME, chatId);
    await updateDoc(chatRef, {
      followUpQuestions: arrayUnion(followUpData),
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error("Error adding follow-up: ", error);
    throw error;
  }
};

export const getUserHistory = async (userId: string) => {
  try {
    // Use nested collection path: users/{userId}/chatHistory
    const q = query(
      collection(db, 'users', userId, COLLECTION_NAME),
      orderBy('timestamp', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      const timestamp = data.timestamp || data.createdAt || data.updatedAt;
      const hexagram = data.hexagram || data.hexagrams || null;
      return {
        id: doc.id,
        ...data,
        hexagram,
        hexagrams: data.hexagrams || hexagram,
        timestamp,
        createdAt: data.createdAt || timestamp,
        updatedAt: data.updatedAt || timestamp,
        followUpQuestions: data.followUpQuestions || []
      };
    }) as ChatSession[];
  } catch (error) {
    console.error("Error fetching user history: ", error);
    throw error;
  }
};
